import { useEffect, useMemo, useState } from 'react';
import {
  useCreateTransform,
  useGetTransform,
  useGetTransforms,
  useUpdateTransform
} from '../../../../../services/workflow.services';
import { cloneDeep, isNumber } from 'lodash';
import transformationStore from '../../../transformation.store';
import { normalizeTransformation } from './helpers/normalizer';
import { unnormalizeTranformation } from './helpers/unnormalizer';
import { mapBaseCurrencyCodes } from './helpers';

export function useHook({
  activeTab,
  extractId,
  setTransformationData,
  extractedTransformationTable,
  onChangeExtractedTransformationTable
}) {
  const createTransform = useCreateTransform({});
  const updateTransform = useUpdateTransform({});

  const { tableNames, setTableNames } = transformationStore;

  const [stepIn, setStepIn] = useState();
  const [amongIndex, setAmongIndex] = useState();
  const [isOriginalView, setIsOriginalView] = useState(false);
  const [tranformationId, setTransformationId] = useState('');
  const [isNewAction, setIsNewAction] = useState(false);
  const [transformations, setTransformations] = useState([]);
  const [selectedTransofrmId, setSelectedTransformId] = useState(null);

  const { data, refetch: refetchList } = useGetTransforms({
    params: {
      extract_id: extractId
    },
    queryParams: {
      enabled: !!extractId
    }
  });

  const {
    data: singleTransformation,
    refetch: refetchSingleTransformation,
    isFetching,
    isLoading
  } = useGetTransform({
    transformId: tranformationId,
    params: {
      step_in: isOriginalView ? 0 : stepIn
    },
    queryParams: {
      enabled: !!tranformationId
    }
  });

  const prevColumnsIndex = useMemo(() => {
    if (amongIndex === null || amongIndex === undefined) return Number(stepIn) - 1;

    return Number(amongIndex) + 1;
  }, [isOriginalView, amongIndex, stepIn]);

  const { data: transformationForColumns } = useGetTransform({
    transformId: tranformationId,
    params: {
      step_in: prevColumnsIndex
    },
    queryParams: {
      enabled: !isNaN(prevColumnsIndex)
    }
  });

  const toggleOriginalView = () => {
    setIsOriginalView(prev => {
      setAmongIndex();

      if (prev) setStepIn(transformations?.length || null);
      else setStepIn();
      return !prev;
    });
  };

  useEffect(() => {
    if (activeTab === 'extraction') return;
    if (!data) return;
    if (!extractedTransformationTable) return;

    const table = data?.find(el => el.table_name === extractedTransformationTable);
    setTransformationId(table?.id);
  }, [extractedTransformationTable, data, activeTab]);

  useEffect(() => {
    if (!data || !data?.length) return;

    setTableNames(
      [
        ...new Set(data?.filter(el => el.extract_id === extractId)?.map(tb => tb.table_name))
      ]?.filter(el => el) || []
    );
  }, [data, extractId]);

  const foundItem = useMemo(() => {
    if (!data) return null;

    let _data = [];
    const currentItem = data.find(item => item.table_name === extractedTransformationTable);

    if (singleTransformation && tranformationId) {
      _data = cloneDeep(singleTransformation?.data);
    } else {
      _data = cloneDeep(currentItem?.data);
    }

    return { ...currentItem, data: mapBaseCurrencyCodes(_data) };
  }, [data, extractedTransformationTable, singleTransformation, tranformationId]);

  const transformationSteps = useMemo(() => {
    if (!foundItem) return [];

    return (
      foundItem.steps?.map(step => {
        const unnormalized = unnormalizeTranformation(step, step.id);

        return {
          id: step.id,
          name: step.action,
          error: step.error,
          note: step.note,
          auto_note: step.auto_note,
          data: foundItem.data,
          items:
            Array.isArray(unnormalized) && unnormalized.length > 0 ? unnormalized : [unnormalized]
        };
      }) || []
    );
  }, [foundItem]);

  useEffect(() => {
    setTransformations(transformationSteps);
  }, [transformationSteps]);

  useEffect(() => {
    if (!foundItem) {
      setTransformationData([]);
      return;
    }
    setTransformationData(foundItem.data);
  }, [foundItem]);

  const refetch = () => {
    refetchList();
    refetchSingleTransformation();
  };

  useEffect(() => {
    if (extractedTransformationTable || !tableNames) return;
    if (tableNames.includes(extractedTransformationTable)) return;

    onChangeExtractedTransformationTable(tableNames?.[0]);
    setStepIn();
  }, [extractedTransformationTable, extractId]);

  const columns = useMemo(() => {
    if (!data || !extractedTransformationTable) return [];

    const foundItem = data?.find(item => item.table_name === extractedTransformationTable);

    if (!foundItem) return [];

    setSelectedTransformId(foundItem.id);

    if (transformationForColumns && !isNewAction && stepIn !== foundItem?.steps?.length) {
      return Object.values(transformationForColumns.data[0]);
    }

    return Object.values(foundItem.data[0]);
  }, [data, extractedTransformationTable, transformationForColumns, isNewAction, stepIn]);

  const addTransformation = (transformation, queryParam) => {
    handleCreateTransform(transformation, queryParam);
  };

  const editTransformation = (transformation, idx, queryParam) => {
    handleUpdateTransform(transformation, idx, queryParam);
  };

  const removeTransformation = (idx, queryParam) => {
    setTransformations(prev => {
      const _filtered = prev.filter((_, index) => idx !== index);

      const _steps = _filtered.map(el => normalizeTransformation(el.items)?.steps[0]);

      updateTransform.mutate(
        {
          transformId: selectedTransofrmId,
          data: {
            steps: _steps
          }
        },
        {
          onSuccess: () => {
            queryParam?.onSuccess();
            refetch();
          }
        }
      );

      return prev;
    });
  };

  const handleCreateTransform = (transformation, queryParam) => {
    const existingTransformation = data?.find(
      item => item.table_name === extractedTransformationTable && item.id
    );

    if (existingTransformation) return handleUpdateTransform(transformation, null, queryParam);

    const _payload = normalizeTransformation(transformation);

    createTransform.mutate(
      {
        extractId,
        tableName: extractedTransformationTable,
        data: {
          steps: [..._payload?.steps],
          table_name: extractedTransformationTable
        }
      },
      {
        ...queryParam,
        onSuccess: res => {
          queryParam.onSuccess(res);
          setIsOriginalView(false);
          setStepIn(res?.steps?.length);
          refetch();
        }
      }
    );
  };

  const handleUpdateTransform = (transformation, idx, queryParam) => {
    const existingTransformation = data?.find(
      item => item.table_name === extractedTransformationTable && item.id
    );

    const _payload = transformation?.length > 0 ? normalizeTransformation(transformation) : [];

    const isNew = transformation?.length > 0 && transformation[transformation?.length - 1]?.isNew;
    let _steps = [...(_payload?.steps || [])];

    if (amongIndex !== null && amongIndex !== undefined)
      _steps = [
        ...existingTransformation?.steps.slice(0, amongIndex + 1),
        ..._steps,
        ...existingTransformation?.steps.slice(amongIndex + 1)
      ];
    else if (isNew) _steps = [...existingTransformation?.steps, ..._steps];

    if (idx !== null && idx !== undefined)
      _steps = existingTransformation?.steps?.map((step, sIdx) =>
        sIdx === idx ? _payload?.steps[0] : step
      );

    updateTransform.mutate(
      {
        transformId: selectedTransofrmId,
        data: {
          steps: _steps
        }
      },
      {
        ...queryParam,
        onSuccess: res => {
          const currentActionIndex = isNumber(amongIndex)
            ? amongIndex + 2
            : stepIn || res.steps?.length;

          queryParam.onSuccess(res);
          setStepIn(isNewAction ? res?.steps?.length : currentActionIndex);
          refetch();
        }
      }
    );
  };

  const handleSaveTransformations = _transformations => {
    let _steps = [];

    _transformations.forEach(_transf => {
      const _payload = normalizeTransformation(_transf.items, _transf.systemId) || [];

      _steps = [..._steps, ...(_payload?.steps || [])];
    });

    if (selectedTransofrmId)
      updateTransform.mutate(
        {
          transformId: selectedTransofrmId,
          data: {
            steps: _steps
          }
        },
        {
          onSuccess: _ => {
            refetch();
          }
        }
      );
    else
      createTransform.mutate(
        {
          extractId,
          tableName: extractedTransformationTable,
          data: {
            steps: _steps,
            table_name: extractedTransformationTable
          }
        },
        {
          onSuccess: _ => {
            refetch();
          }
        }
      );
  };

  const toggleStepIn = idx => {
    setStepIn(prev => {
      setIsOriginalView(false);

      return idx;
    });
  };

  useEffect(() => {
    if (!transformations || transformations?.length === 0) return;

    if (!isOriginalView)
      setStepIn(prev => {
        if (!prev) return transformations?.length;

        return prev;
      });
  }, [transformations, isOriginalView]);

  const columnsTypes = useMemo(() => {
    if (!singleTransformation)
      return data?.length > 0 ? Object.values(data[0]?.columns_types || []) : [];
    return Object.values(singleTransformation?.columns_types || []);
  }, [singleTransformation, data]);

  return {
    stepIn,
    columns,
    isLoading,
    isFetching,
    isOriginalView,
    amongIndex,
    transformations,
    tranformationId,
    isNewAction,
    isLoadingTransformationPatch: updateTransform.isLoading || createTransform.isLoading,
    setIsNewAction,
    toggleStepIn,
    setAmongIndex,
    addTransformation,
    editTransformation,
    toggleOriginalView,
    removeTransformation,
    handleSaveTransformations,
    setTransformations,
    refetch,
    setTableName: onChangeExtractedTransformationTable,
    columnsTypes
  };
}
